import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HeartHandshake, MapPin, ShieldCheck, PackagePlus, ArrowRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HUMAN_INTENTS, ENGAGEMENT_TYPES, findOptLabel } from "@/lib/listingDyn";
import { StoryCard } from "@/components/SuccessStories";

const PLACEHOLDER = "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?crop=entropy&cs=srgb&fm=jpg&q=80&w=940";
const OFFER_GROUP = ["oferta_mentor", "donatie_utilaj"];
const SEEK_GROUP = ["caut_ucenic", "caut_loc_munca_ucenic", "muncitor_calificat", "ajutor_muncitor"];

function OrphanCard({ l, lng, t }) {
  const ds = l.dynamic_specs || {};
  const hi = ds.human_intent;
  const eng = ds.engagement_type;
  return (
    <Link to={`/anunt/${l.id}`} className="group border border-slate-200 bg-white flex flex-col hover:border-emerald-500 transition-colors" data-testid={`arva-card-${l.id}`}>
      <div className="relative overflow-hidden bg-slate-100 aspect-[4/3]">
        <img src={mediaUrl(l.image) || PLACEHOLDER} alt={l.name} loading="lazy"
          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {hi && (
          <span className="absolute top-0 left-0 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5" data-testid="arva-intent-badge">
            {findOptLabel(HUMAN_INTENTS, hi, lng)}
          </span>
        )}
        {ds.is_vetted && (
          <span className="absolute top-0 right-0 bg-amber-500 text-slate-900 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> {t("arva.vetted")}
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        {ds.craft_type && <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700 mb-1">{ds.craft_type}</span>}
        <h3 className="font-bold text-slate-900 leading-snug mb-3 line-clamp-2 group-hover:text-emerald-700 transition-colors">{l.name}</h3>
        {(l.location || l.county) && (
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-2"><MapPin className="w-3.5 h-3.5" /> {l.location || l.county}</div>
        )}
        {eng && <div className="text-xs text-slate-400 mb-3">{findOptLabel(ENGAGEMENT_TYPES, eng, lng)}</div>}
        <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between text-sm font-bold text-emerald-700">
          {t("arva.detail")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export default function ArvaSzakmak() {
  const { t, i18n } = useTranslation();
  const lng = (i18n.language || "ro").slice(0, 2);
  const { user } = useAuth();
  const [active, setActive] = useState("");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    api.get("/arva-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/success-stories").then((r) => setStories(r.data)).catch(() => {});
  }, []);

  const fetchListings = useCallback(() => {
    setLoading(true);
    const params = { mk_primary: "mestesuguri-traditii" };
    if (active) params.human_intent = active;
    api.get("/listings", { params }).then((r) => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [active]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const Chip = ({ v, label }) => (
    <button onClick={() => setActive((a) => (a === v ? "" : v))}
      className={`text-xs font-bold uppercase tracking-wide px-3 py-2 border transition-colors ${active === v ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 text-slate-600 hover:border-emerald-400 hover:text-emerald-700"}`}
      data-testid={`arva-filter-${v || "all"}`}>{label}</button>
  );

  return (
    <div data-testid="arva-szakmak-page">
      {/* Hero */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-4" data-testid="arva-eyebrow">
            <HeartHandshake className="w-4 h-4" /> {t("arva.eyebrow")}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-3xl" data-testid="arva-title">{t("arva.title")}</h1>
          <p className="text-lg text-slate-300 mt-4 max-w-2xl" data-testid="arva-subtitle">{t("arva.subtitle")}</p>
          <p className="text-sm text-slate-400 mt-6 max-w-3xl leading-relaxed border-l-2 border-emerald-500 pl-4">{t("arva.intro")}</p>
          <Link to={user ? "/vinde" : "/autentificare"} className="inline-flex items-center gap-2 mt-8 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3.5 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="arva-cta">
            <PackagePlus className="w-4 h-4" /> {t("arva.cta")}
          </Link>

          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-700/60 border border-slate-700/60 mt-12 max-w-3xl" data-testid="arva-stats">
              {[
                { k: "mentors", v: stats.mentors, label: t("arva.statMentors") },
                { k: "talents", v: stats.talents, label: t("arva.statTalents") },
                { k: "donations", v: stats.donations, label: t("arva.statDonations") },
                { k: "total", v: stats.total, label: t("arva.statTotal") },
              ].map((s) => (
                <div key={s.k} className="bg-slate-900 px-5 py-5" data-testid={`arva-stat-${s.k}`}>
                  <div className="text-3xl lg:text-4xl font-black text-emerald-400 tracking-tight">{s.v}</div>
                  <div className="text-[11px] uppercase tracking-wide font-bold text-slate-400 mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lifetime impact tally */}
      {stats?.lifetime && (
        <div className="bg-emerald-50 border-b border-emerald-100" data-testid="arva-lifetime">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700 mb-6">{t("arva.lifetimeTitle")}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { k: "apprentices_placed", label: t("arva.lifetimeApprentices") },
                { k: "tools_donated", label: t("arva.lifetimeTools") },
                { k: "mentors_engaged", label: t("arva.lifetimeMentors") },
                { k: "workshops_supported", label: t("arva.lifetimeWorkshops") },
              ].map((s) => (
                <div key={s.k} data-testid={`arva-lifetime-${s.k}`}>
                  <div className="text-4xl lg:text-5xl font-black text-emerald-700 tracking-tight">{stats.lifetime[s.k]}</div>
                  <div className="text-xs uppercase tracking-wide font-bold text-slate-500 mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-start gap-x-8 gap-y-4 mb-8" data-testid="arva-filters">
          <Chip v="" label={t("arva.all")} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">{t("arva.offerTitle")}</div>
            <div className="flex flex-wrap gap-2">
              {OFFER_GROUP.map((v) => <Chip key={v} v={v} label={findOptLabel(HUMAN_INTENTS, v, lng)} />)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">{t("arva.seekTitle")}</div>
            <div className="flex flex-wrap gap-2">
              {SEEK_GROUP.map((v) => <Chip key={v} v={v} label={findOptLabel(HUMAN_INTENTS, v, lng)} />)}
            </div>
          </div>
        </div>

        <div className="text-sm text-slate-500 mb-4" data-testid="arva-count">{loading ? "…" : `${listings.length} ${t("arva.countLabel")}`}</div>

        {!loading && listings.length === 0 ? (
          <div className="border border-slate-200 p-12 text-center" data-testid="arva-empty">
            <p className="text-slate-500">{t("arva.empty")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map((l) => <OrphanCard key={l.id} l={l} lng={lng} t={t} />)}
          </div>
        )}
      </div>

      {/* Success stories */}
      {stories.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-200" data-testid="arva-stories">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-slate-900">{t("arva.storiesTitle")}</h2>
            <p className="text-slate-500 mt-2 mb-10">{t("arva.storiesSubtitle")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {stories.map((s) => <StoryCard key={s.id} s={s} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
